<template>
    <div class="left-panel flex-v">
        <div class="panel-heading block">
            <h3>Устройства</h3>
            <Spinner v-if="this.isLoading" style="margin-left: 8px;" />
            <v-spacer />

            <v-tooltip bottom>
                <template v-slot:activator="{on, attrs}">
                    <v-btn icon @click="isHiddenShown = !isHiddenShown" v-bind="attrs" v-on="on">
                        <v-icon :color="isHiddenShown ? 'primary' : ''">
                            mdi-eye
                        </v-icon>
                    </v-btn>
                </template>
                <span>Показывать скрытые</span>
            </v-tooltip>

            <v-tooltip bottom>
                <template v-slot:activator="{on, attrs}">
                    <v-btn icon @click="isCompactMode = !isCompactMode" v-bind="attrs" v-on="on">
                        <v-icon :color="isCompactMode ? 'primary' : ''">
                            mdi-format-vertical-align-center
                        </v-icon>
                    </v-btn>
                </template>
                <span>Компактный вид</span>
            </v-tooltip>
        </div>

        <div class="device-list flex-fill" :class="{compact: isCompactMode, detailed: !isCompactMode}">
            <div v-if="groupedDevices.length === 0" class="no-items">
                Нет подключенных устройств
            </div>

            <v-expansion-panels multiple v-model="openedGroupIndexes">
                <v-expansion-panel v-for="group in groupedDevices" :key="group.id"
                                   class="group-item"
                >
                    <v-expansion-panel-header class="group-name">
                        {{ group.name }}
                    </v-expansion-panel-header>

                    <v-expansion-panel-content>
                        <SidebarDevice
                            v-for="device in group.devices" :key="device.id"
                            :device="device"
                            :is-compact="isCompactMode"
                            @click="onDeviceClick(device)"
                        />
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </div>
    </div>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'
import {Prop} from 'vue-property-decorator'
import {Device} from '@/models/Device'
import SidebarDevice from '@/views/overview/SidebarDevice.vue'
import {DeviceGroup} from '@/models/DeviceGroup'
import Spinner from '@/components/Spinner.vue'

interface ListGroup
{
    id: number
    name: string
    devices: Device[]
}

@Component({
    components: {Spinner, SidebarDevice},
})
export default class OverviewSidebar extends Vue
{
    @Prop(Array)
    devices!: Device[]

    @Prop(Boolean)
    isLoading!: boolean

    get groupedDevices(): ListGroup[] {
        const noGroup: ListGroup = {id: -1, name: 'Без группы', devices: []}
        const groups: ListGroup[] = []

        function getOrCreateGroup(group?: DeviceGroup) {
            if (!group) {
                return noGroup
            }

            const found = groups.find(g => g.id === group.id)
            if (found) {
                return found
            }

            const created: ListGroup = {id: group.id as number, name: group.name, devices: []}
            groups.push({...created})
            return created
        }

        this.devices.forEach(device => {
            const group = getOrCreateGroup(device.group)
            group.devices.push(device)
        })

        if (noGroup.devices.length > 0) {
            groups.unshift(noGroup)
        }

        return groups
    }

    get isCompactMode() {
        return this.$store.state.settings?.schemeView?.isCompactMode ?? false
    }

    set isCompactMode(value: boolean) {
        this.$store.commit('setSetting', ['schemeView.isCompactMode', value])
    }

    get isHiddenShown() {
        return this.$store.state.settings?.schemeView?.isHiddenShown ?? false
    }

    set isHiddenShown(value: boolean) {
        this.$store.commit('setSetting', ['schemeView.isHiddenShown', value])
    }

    get openedGroupIds(): number[] {
        return this.$store.state.settings?.schemeView?.openedGroupIds ?? this.groupedDevices.map(g => g.id)
    }

    set openedGroupIds(ids: number[]) {
        this.$store.commit('setSetting', ['schemeView.openedGroupIds', ids])
    }

    get openedGroupIndexes(): number[] {
        return this.openedGroupIds
            .map(id => this.groupedDevices.findIndex(g => g.id === id))
            .filter(idx => idx !== -1)
    }

    // noinspection JSUnusedGlobalSymbols
    set openedGroupIndexes(indexes: number[]) {
        this.openedGroupIds = indexes
            .map(idx => this.groupedDevices[idx]?.id)
            .filter(id => id != null)
    }

    onDeviceClick(device: Device) {
        this.$emit('device-click', device)
    }
}
</script>
